import {
	IsArray,
	IsBoolean,
	IsEnum,
	IsInt,
	IsNotEmpty,
	IsNumber,
	IsOptional,
	IsString,
	IsUrl,
	IsUUID,
	Min,
	ValidateIf,
	ValidateNested,
} from 'class-validator';
import { Transform, Type } from 'class-transformer';
import { PrivacyTypesEnum, EventPolicyTypesEnum, EventPublishingStateEnum } from '../../../../enums';
import { FileUploadDto } from '../../../../models/dto/file.dto';
import { type EventIntakeFormSettings } from '../../../../models/dto/intake-form-settings.dto';
import { EventPeriodType, type EventAdditionalData, CreateScheduleRepeat, type EventDraftData } from '../../../../models/dto/event.dto';
import { ALLOWED_EVENT_AVATAR_MIME_TYPES } from '../../../../constants';
import { MaxBase64FileSize, Base64AllowedMimeTypes } from '../../../../decorators';
import { dateStringToDateOptional } from '../../../../types-transformers';
import { SetAddressDto } from '../../../_core/models/input/address.dto';
import { SetEventPriceDto } from './event-price.dto';
import { SetEventDiscountDto } from './event-discount.dto';
import { EditEventLeaderDto } from './edit-event-leader.dto';
import { EditEventSponsorDto } from './edit-event-sponsor.dto';
import { EventDonationLimitsPerMembershipDto } from './event-donation-limits-per-membership.dto';
import { SetEventToFitnessTypeDto } from './event-fitness-type.dto';

/**
 * @note as a general rule, if field is not passed, it remains untouched.
 *  To set value to null, pass `null` explicitly where applicable.
 *  For array-like fields, pass an empty array `[]` to remove all items.
 */
export class UpdateEventV2Dto {
	/**
	 * @description events.id field value.
	 * @note Stands for id of the current event version received from DB for editing.
	 *  If passed, it will be used to verify the frontend attempts to update the actual version of the event.
	 */
	@IsOptional()
	@IsUUID()
	public id?: string;

	@IsOptional()
	@IsEnum(EventPublishingStateEnum)
	public publishingState?: EventPublishingStateEnum;

	@IsOptional()
	@IsEnum(PrivacyTypesEnum)
	@IsNotEmpty()
	public privacyId?: PrivacyTypesEnum;

	@IsOptional()
	@IsString()
	@IsNotEmpty({
		message: 'Event name should not be empty',
	})
	public name?: string;

	@IsOptional()
	@IsString()
	public description?: string | null;

	@IsOptional()
	public draftData?: EventDraftData | null;

	@IsOptional()
	@IsString()
	public authorId?: string | null;

	@IsOptional()
	@IsUUID()
	public coHostId?: string | null;

	@IsOptional()
	@IsEnum(EventPolicyTypesEnum)
	public policyType?: EventPolicyTypesEnum;

	@IsOptional()
	@Min(1)
	public attendeesLimit?: number | null;

	@IsOptional()
	@IsBoolean()
	public isRsvpListHidden?: boolean;

	@IsOptional()
	@ValidateIf(o => !o.isOnlineEvent)
	@ValidateNested()
	@Type(() => SetAddressDto)
	public address?: SetAddressDto;

	@IsOptional()
	@IsBoolean()
	public isOnlineEvent?: boolean;

	@IsOptional()
	@ValidateIf(o => o.isOnlineEvent)
	@IsUrl({}, { message: '"Event URL" link should be a valid URL' })
	public onlineUrl?: string;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => SetEventToFitnessTypeDto)
	public setFitnessTypes?: SetEventToFitnessTypeDto[];

	@IsNotEmpty({
		message: 'Start date should not be empty',
	})
	@Transform(dateStringToDateOptional)
	public startDate?: Date;

	@IsOptional()
	@Transform(dateStringToDateOptional)
	public endDate?: Date;

	@IsOptional()
	@IsString()
	public tzid?: string;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => EventDonationLimitsPerMembershipDto)
	public eventDonationLimitsPerMembership?: EventDonationLimitsPerMembershipDto[];

	@IsOptional()
	public allowCheckoutForNDaysAfterEventEnded?: number;

	@IsOptional()
	@IsBoolean()
	public allowToPublishToOtherPlatforms?: boolean;

	@IsOptional()
	@IsString()
	public subHeaderMarkdown?: string | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_EVENT_AVATAR_MIME_TYPES)
	public avatarImg?: FileUploadDto;

	@IsOptional()
	@IsUUID()
	public avatarId?: string | null;

	// TODO refactor these fields
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	public uploadImages?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@IsUUID(4, { each: true })
	public deleteImages?: string[];

	@IsOptional()
	@IsUrl({}, { message: '"Waiver" link should be a valid URL' })
	public waiverUrl?: string | null;

	@IsOptional()
	@IsBoolean()
	public isEditorPick?: boolean;

	@IsOptional()
	public isDonationBased?: boolean;

	/**
	 * @note To get $1.00 pass 1 (not 100)
	 */
	@IsOptional()
	@IsNumber()
	@IsInt()
	@Min(0)
	public minimalDonationAmount?: number | null;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => EditEventSponsorDto)
	public sponsors?: EditEventSponsorDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => EditEventLeaderDto)
	public leaders?: EditEventLeaderDto[];

	@IsOptional()
	@IsArray()
	public eventBundleIds?: string[];

	@IsOptional()
	@IsBoolean()
	public onlySingleAttendanceAllowed?: boolean;

	@IsOptional()
	public intakeFormSettings?: EventIntakeFormSettings;

	@IsOptional()
	public additionalData?: EventAdditionalData;

	@IsOptional()
	@IsArray()
	public invitedUserIds?: string[];

	@IsOptional()
	@ValidateNested()
	@Type(() => CreateScheduleRepeat)
	public scheduleRepeat?: CreateScheduleRepeat;

	/**
	 * @note When value changes, chat room creates/removes accordingly.
	 */
	@IsOptional()
	@IsBoolean()
	public autoGenerateEventChat?: boolean;

	@IsOptional()
	public eventChatCid?: string | null;

	@IsOptional()
	@IsNotEmpty()
	@IsEnum(EventPeriodType)
	public type?: EventPeriodType;

	@IsOptional()
	@IsNotEmpty()
	public instance?: Date;

	@IsOptional()
	@IsBoolean()
	public isVerified?: boolean;

	@IsOptional()
	@IsBoolean()
	public isWaitlistEnabled?: boolean;

	@IsOptional()
	@IsBoolean()
	public isPaid?: boolean;

	/**
	 * @description
	 * Set of prices to rewrite. Empty array value will remove all prices. If price is not present in the list, it will be deleted.
	 *
	 * If price is present in the list, it will be either updated (using `id` field) or created
	 *
	 * @note When field is not passed, it remains untouched.
	 */
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => SetEventPriceDto)
	public setPrices?: SetEventPriceDto[];

	/**
	 * @description
	 * Set of discounts to rewrite. Empty array value will remove all discounts. If discount is not present in the list, it will be deleted.
	 *
	 * If discount is present in the list, it will be either updated (using `id` field) or created.
	 *
	 * Assign discount to prices using `price.basePriceId` field
	 *
	 * @note When field is not passed, it remains untouched.
	 */
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => SetEventDiscountDto)
	public setDiscounts?: SetEventDiscountDto[];

	/**
	 * @description
	 * Set media for event. If object has id then existing media will be attached to the event. Otherwise, new media will be firstly created and then attached.
	 *
	 * Empty array value will remove all media. If media id is not present in the list, it will be deleted.
	 *
	 * Prefer using `FileUploadDto.key` field to upload a media instead of deprecated `FileUploadDto.base64EncodedFile` field.
	 */
	public setMedia?: FileUploadDto[];
}
